/**
 * The case-page owner control — the header affordance for case-level
 * analyst ownership. Unclaimed: a quiet "Claim case" button. Claimed:
 * an owner chip with a menu (claim for me / reassign / release).
 *
 * Ownership is family-wide (POST /cases/{family}/claim writes the
 * enquiry + every request); names resolve from the ANALYST roster (the
 * current user's own staff team), with the bare user id as fallback.
 */

import { useState } from 'react';

import { Button, Menu, Text } from '@mantine/core';
import { IconChevronDown, IconUserPlus } from '@tabler/icons-react';

import { useAuth } from '@/contexts/AuthContext';
import { useAnalystRoster } from '@/hooks';
import { useClaimCase } from '@/hooks/useClaimCase';
import type { CaseDetail } from '@/types/case';

interface CaseOwnerControlProps {
  caseDetail: CaseDetail;
  /** Refetch after a successful claim/release. */
  onChanged: () => void | Promise<void>;
}

export function CaseOwnerControl({ caseDetail, onChanged }: Readonly<CaseOwnerControlProps>) {
  const auth = useAuth();
  const currentUserId = auth?.user?.id != null ? String(auth.user.id) : null;
  const members = useAnalystRoster();
  const { claim } = useClaimCase();
  const [busy, setBusy] = useState(false);

  if (!caseDetail.claimable) {
    return null;
  }

  const owner = caseDetail.assignedAnalystUserId;
  const nameFor = (userId: string) => members.find((m) => String(m.id) === userId)?.name ?? userId;

  const claimAs = async (userId: string | null) => {
    setBusy(true);
    try {
      await claim(caseDetail.familyKey, userId);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (!owner) {
    return (
      <Button.Group>
        <Button
          variant="default"
          size="compact-xs"
          loading={busy}
          disabled={!currentUserId}
          leftSection={<IconUserPlus size={12} />}
          onClick={() => void claimAs(currentUserId)}
          data-testid="case-claim"
        >
          Claim case
        </Button>
        <Menu withinPortal position="bottom-start">
          <Menu.Target>
            <Button
              variant="default"
              size="compact-xs"
              px={4}
              aria-label="Assign case to…"
              data-testid="case-assign-menu"
            >
              <IconChevronDown size={12} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Assign case to…</Menu.Label>
            {members.length === 0 && <Menu.Item disabled>No team members</Menu.Item>}
            {members.map((m) => (
              <Menu.Item key={m.id} onClick={() => void claimAs(String(m.id))}>
                {m.name}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    );
  }

  return (
    <Menu withinPortal position="bottom-start">
      <Menu.Target>
        <Button
          variant="default"
          size="compact-xs"
          loading={busy}
          rightSection={<IconChevronDown size={12} />}
          data-testid="case-owner"
        >
          <Text component="span" size="xs" c="dimmed" mr={4}>
            Analyst
          </Text>
          {owner === currentUserId ? 'you' : nameFor(owner)}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {currentUserId && owner !== currentUserId && (
          <Menu.Item onClick={() => void claimAs(currentUserId)}>Claim for me</Menu.Item>
        )}
        {members
          .filter((m) => String(m.id) !== owner)
          .map((m) => (
            <Menu.Item key={m.id} onClick={() => void claimAs(String(m.id))}>
              Reassign to {m.name}
            </Menu.Item>
          ))}
        <Menu.Divider />
        <Menu.Item color="red" onClick={() => void claimAs(null)}>
          Release case
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
